import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, defer, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { noTimeoutContext } from '../http/request-timeout';
import {
  GENERIC_ERROR_MESSAGE,
  GozemApiError,
  SESSION_EXPIRED_MESSAGE,
  toGozemApiError,
} from '@shared/utils/http-error.util';
import { isUsablePhoneNumber } from '@shared/utils/phone.util';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { GozemMoneyAccountStore } from '../stores/gozem-money-account.store';
import type {
  SendAuthTxnRequest,
  SendAuthTxnResponse,
  InitTransferRequest,
  InitTransferParams,
  SendGozemResponse,
  InitPspTransferRequest,
  InitPspTransferParams,
  SendPspResponse,
  SendBasePayload,
  RecipientsResponse,
} from '../models/send.model';
import type {
  TopupMethodsRequest,
  TopupMethodsResponse,
  TopupTokenResponse,
} from '../models/topup.model';

/**
 * SendApi — HTTP calls for the "Envoyer de l'argent" flow.
 *
 * Four endpoints in order of the dialog steps:
 *  1. POST /api/topup_withdrawal_method (Transfer)        → available methods for the amount
 *  2. POST /api/merchant/gozem_money/auth_txn             → validate, compute fee + categories
 *  3a. POST /api/gozem_money/merchant/init_transfer        → Gozem Money path (is_gozem_money_processor = true)
 *  3b. POST /api/gozem_money/merchant/init_psp_transfer    → PSP path (is_gozem_money_processor = false)
 *
 * There is no saved-recipient endpoint. The beneficiary phone is entered
 * manually and passed to auth_txn as an optional parameter.
 *
 * Every method that reads credentials wraps its payload construction in `defer()` so a
 * synchronous throw from `basePayload()` surfaces as an error *notification* on the
 * returned stream rather than escaping the caller's `switchMap` projection — the latter
 * bypasses the store's inner `catchError`, permanently tears down the pipeline and leaves
 * the dialog stuck on a loading flag with no request sent.
 */
@Injectable({
  providedIn: 'root',
})
export class SendApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);
  private readonly gmAccountStore = inject(GozemMoneyAccountStore);

  // ── 1. Methods ─────────────────────────────────────────────────────────────

  /**
   * Fetch transfer methods available for the given amount.
   * No beneficiary phone required — the backend filters by amount only.
   * POST /api/topup_withdrawal_method with operation_type: 'Transfer'
   */
  getMethods(amount: number): Observable<TopupMethodsResponse> {
    return defer(() => {
      const payload: TopupMethodsRequest = {
        ...this.basePayload(),
        amount,
        operation_type: 'Transfer',
      };
      return this.http.post<TopupMethodsResponse>(
        `${environment.apiUrl}/api/topup_withdrawal_method`,
        payload,
      );
    }).pipe(catchError((err) => this.handleError(err)));
  }

  // ── 2a. Tokens (saved accounts for a processor) ───────────────────────────

  getTokens(processorId: string): Observable<TopupTokenResponse> {
    return defer(() => {
      const payload = {
        ...this.basePayload(),
        processor_id: processorId,
        token_service: 2,
      };
      return this.http.post<TopupTokenResponse>(`${environment.apiUrl}/api/token`, payload);
    }).pipe(catchError((err) => this.handleError(err)));
  }

  // ── 2b. Recipients (Gozem Money recent beneficiaries) ─────────────────────

  getRecipients(): Observable<RecipientsResponse> {
    return this.http
      .get<RecipientsResponse>(`${environment.apiUrl}/api_merchant/v1/recipients`)
      .pipe(catchError((err) => this.handleError(err)));
  }

  // ── 3. Auth transaction ────────────────────────────────────────────────────

  /**
   * Authorize the transfer: validates the amount, computes fees,
   * and returns available categories + whether PIN is required.
   *
   * beneficiaryPhoneNumber is extracted from the selected token's properties
   * (PSP path). Gozem Money path fires without a beneficiary identifier.
   */
  authTxn(
    amount: number,
    processorId: string,
    methodId: string,
    beneficiaryPhoneNumber?: string,
    accountNumber?: string,
  ): Observable<SendAuthTxnResponse> {
    return defer(() => {
      const payload: SendAuthTxnRequest = {
        ...this.basePayload(),
        amount,
        processor_id: processorId,
        txn_type: 'Transfer',
        transaction_method_id: methodId,
        // isUsablePhoneNumber, not truthiness: a bare '+' is a non-empty string and
        // sailed through the old guard, which is what produced the "1032 - Gozem
        // money user not found" rejection observed in staging.
        ...(isUsablePhoneNumber(beneficiaryPhoneNumber)
          ? { beneficiary_phone_number: beneficiaryPhoneNumber }
          : {}),
        ...(accountNumber ? { payee_gm_account_number: accountNumber } : {}),
      };
      return this.http.post<SendAuthTxnResponse>(
        `${environment.apiUrl}/api/merchant/gozem_money/auth_txn`,
        payload,
      );
    }).pipe(catchError((err) => this.handleError(err)));
  }

  // ── 3a. Init Transfer — Gozem Money path ───────────────────────────────────

  /**
   * Execute a Gozem Money internal transfer.
   * payee_gm_account_number is sourced from the auth_txn result.
   * Used when the selected method has is_gozem_money_processor = true.
   */
  initTransfer(params: InitTransferParams): Observable<SendGozemResponse> {
    const {
      amount,
      payeeGmAccountNumber,
      categoryId,
      fees,
      message,
      authenticationData,
      beneficiaryFullName,
      beneficiaryPhoneNumber,
    } = params;
    return defer(() => {
      const payload: InitTransferRequest = {
        ...this.basePayload(),
        amount,
        txn_type: 'Transfer',
        payee_gm_account_number: payeeGmAccountNumber,
        ...(beneficiaryFullName ? { beneficiary_full_name: beneficiaryFullName } : {}),
        ...(isUsablePhoneNumber(beneficiaryPhoneNumber)
          ? { beneficiary_phone_number: beneficiaryPhoneNumber }
          : {}),
        ...(categoryId ? { category: categoryId } : {}),
        shared_note: message,
        fee: fees.fee,
        commission: fees.commission,
        payment_tags: [],
        payment_references: [],
        ...(authenticationData ? { authentication_data: authenticationData } : {}),
      };
      // No client-side timeout: this call moves money, so aborting our wait can
      // never be safe — see `noTimeoutContext`.
      return this.http.post<SendGozemResponse>(
        `${environment.apiUrl}/api/gozem_money/merchant/init_transfer`,
        payload,
        { context: noTimeoutContext() },
      );
    }).pipe(catchError((err) => this.handleError(err)));
  }

  // ── 3b. Init PSP Transfer — PSP path ──────────────────────────────────────

  /**
   * Execute a PSP-backed transfer (e.g. Mixx, Moov, Orange, Wave).
   * Used when the selected method has is_gozem_money_processor = false.
   */
  initPspTransfer(params: InitPspTransferParams): Observable<SendPspResponse> {
    const {
      amount,
      processorId,
      tokenId,
      withdrawalId,
      categoryId,
      fees,
      message,
      authenticationData,
    } = params;
    return defer(() => {
      const payload: InitPspTransferRequest = {
        ...this.basePayload(),
        amount,
        processor_id: processorId,
        token_id: tokenId,
        withdrawal_id: withdrawalId,
        txn_type: 'Transfer',
        ...(categoryId ? { category: categoryId } : {}),
        shared_note: message,
        fee: fees.fee,
        commission: fees.commission,
        payment_tags: [],
        payment_references: [],
        ...(authenticationData ? { authentication_data: authenticationData } : {}),
      };
      // No client-side timeout: this call moves money, so aborting our wait can
      // never be safe — see `noTimeoutContext`.
      return this.http.post<SendPspResponse>(
        `${environment.apiUrl}/api/gozem_money/merchant/init_psp_transfer`,
        payload,
        { context: noTimeoutContext() },
      );
    }).pipe(catchError((err) => this.handleError(err)));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private basePayload(): SendBasePayload {
    const userData = this.storage.getUserData();
    if (!userData) {
      // Thrown inside each method's `defer()`, so this reaches the caller as a stream
      // error rather than a synchronous throw. Must be a GozemApiError: SendStore only
      // surfaces `err.displayMessage` for that type and would otherwise replace this with
      // the generic "Le transfert n'a pas pu être traité". `remainingAttempts: null` keeps
      // `isAuthStageError()` false, so it is classified as a failure, not a wrong PIN.
      throw new GozemApiError(SESSION_EXPIRED_MESSAGE, 'NO_USER_DATA', null, null);
    }
    return {
      merchant_id: userData.merchantId,
      // Active account, not accounts[0]: pinning to the first row ignores which
      // merchant the user actually selected. See AccountStore.resolveAccountId.
      account_id: this.accountStore.resolveAccountId(),
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      user_gm_account_id: this.gmAccountStore.primaryAccountId(),
    };
  }

  private handleError(error: unknown): Observable<never> {
    // A non-HttpErrorResponse comes from one of our own pre-request guards (basePayload),
    // already shaped as a GozemApiError with user-facing text. `toGozemApiError` only reads
    // HttpErrorResponse fields and would flatten it to the generic fallback — telling the
    // user to retry a call that can never succeed. Forward it verbatim.
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    // Preserve the business code + remaining_attempts metadata so the PIN
    // flows can distinguish a wrong-PIN / lockout error from a transaction
    // failure. Bare `new Error(...)` here was the root cause of the PIN/lock
    // errors being misclassified downstream.
    return throwError(() => toGozemApiError(error, GENERIC_ERROR_MESSAGE));
  }
}
